
import "./Service_pages.scss";
import img from "./services/pediatric_dentistry.png";
import dentalImage from "./services/dental.png";
import { FaCheckCircle, FaTooth, FaSmile, FaChild, FaStar, FaUserMd, FaClinicMedical, FaHeart, FaMagic, FaShieldAlt,  FaBaby } from 'react-icons/fa';
import { MdOutlineHealthAndSafety, MdOutlineStarPurple500, MdHealing } from "react-icons/md";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";

const ChildrenDentistry = () => {

  const services = [
    {
      title: "Fluoride Treatment",
      icon: <MdOutlineHealthAndSafety />,
      desc: "A quick and painless application of fluoride gel or varnish that strengthens young enamel and makes teeth highly resistant to decay.",
    },
    {
      title: "Dental Sealants",
      icon: <FaShieldAlt />,
      desc: "Protective plastic coatings applied to the deep grooves of molars to 'seal out' food particles and cavity-causing bacteria.",
    },
    {
      title: "Baby Root Canal (Pulpotomy)",
      icon: <MdHealing />,
      desc: "When decay reaches the nerve of a primary tooth, we perform a gentle pulpotomy to save the tooth and prevent premature loss.",
    },
    {
      title: "Space Maintainers",
      icon: <FaTooth />,
      desc: "Custom appliances that hold open the space for permanent teeth when a baby tooth is lost too early due to decay or injury.",
    },
    {
      title: "Habit Breaking Appliances",
      icon: <FaMagic />,
      desc: "Specialized devices to help children overcome thumb sucking or tongue thrusting habits that could affect jaw development.",
    },
    {
      title: "Stainless Steel Crowns",
      icon: <MdOutlineStarPurple500 />,
      desc: "Durable, silver-colored crowns used to restore heavily decayed baby teeth until they are naturally replaced by permanent ones.",
    },
  ];

  const benefits = [
    {
      title: "Stress-Free Environment",
      description: "Our clinic is designed to be friendly and inviting, helping children feel at home and reducing dental anxiety from a young age.",
    },
    {
      title: "Preventive-First Approach",
      description: "We focus on stopping cavities before they start through professional cleaning, sealants, and early parent-child education.",
    },
    {
      title: "Specialized Pediatric Care",
      description: "Our team uses child-friendly language and gentle techniques tailored specifically for young, developing mouths and sensitive tissues.",
    },
    {
      title: "Growth Monitoring",
      description: "We track your child's dental development closely to identify early signs of crowding or bite issues, saving time and costs later.",
    },
    {
      title: "Rewards & Positive Reinforcement",
      description: "We celebrate every brave visit with stickers and praise, building a positive association with dental health that lasts a lifetime.",
    },
  ];

  const reasons = [
    {
      icon: <FaChild />,
      title: 'Pediatric Expertise',
      description: 'Specialized care for children, focusing on physical growth and psychological comfort.'
    },
    {
      icon: <FaUserMd />,
      title: 'Gentle Handling',
      description: 'Our dentists use a "Tell-Show-Do" technique to make every step predictable and calm.'
    },
    {
      icon: <FaClinicMedical />,
      title: 'Top-Tier Sterilization',
      description: '100% sterile environment to ensure the safety of your little ones during every visit.'
    },
    {
      icon: <FaHeart />,
      title: 'Parent Partnership',
      description: 'We guide parents on diet, brushing, and habit management to maintain health at home.'
    },
    {
      icon: <FaStar />,
      title: 'Modern Technology',
      description: 'Digital X-rays with ultra-low radiation levels designed specifically for pediatric safety.'
    },
    {
      icon: <FaSmile />,
      title: 'Lifetime of Smiles',
      description: 'Building a foundation of healthy habits and a fear-free attitude toward dentistry.'
    }
  ];

  return (
    <>
      <div className="service1-section">
        <Helmet>
          <title>Children's Dentistry in HSR Layout | Kids Dental Care Bangalore</title>
          <meta
            name="description"
            content="Expert Children's Dentistry in HSR Layout. Kosmix Dental Clinic provides painless treatments, fluoride, and sealants for kids in a friendly environment."
          />
          <link
            rel="canonical"
            href="https://www.kosmixdentalclinic.com/services/children-dentistry-in-hsr-layout"
          />
        </Helmet>

        {/* HERO SECTION */}
        <div className="hero">
          <h1 className="service1-title-text" data-aos="fade-down" data-aos-delay="200" style={{ fontFamily: 'initial' }}>
             Children's Dentistry in HSR Layout
          </h1>

          <div className="hero-content">
            <div className="hero-image" data-aos="zoom-in-right" data-aos-delay="200" data-aos-duration="800">
              <img src={img} alt="Pediatric Dentistry at Kosmix" />
            </div>

            <div className="hero-text" data-aos="zoom-in-left" data-aos-delay="200" data-aos-duration="800">
              <p>
                At <strong>Kosmix Dental Clinic</strong>, we believe that every child deserves a bright, healthy smile and a positive start to their oral health journey. <strong>Children’s dentistry</strong> is more than just fixing teeth; it’s about nurturing a fear-free attitude towards dental care.
              </p>
              <p>
                Our team in HSR Layout specializes in providing gentle, high-quality dental care for children of all ages—from toddlers to teenagers. We combine a playful, friendly environment with modern clinical expertise to ensure your child feels safe and comfortable.
              </p>
              <p>
                Whether it's your child's first routine checkup or a restorative treatment like a pediatric filling, we focus on preventive care, patient education, and painless experiences to build a foundation for a lifetime of healthy smiles.
              </p>
            </div>
          </div>
        </div>

        {/* DEFINITION SECTION */}
        <div className="extraction-definition-card" data-aos="fade-up" data-aos-delay="300">
          <SectionTitle title="Why Early Pediatric Care Matters?" />
          <p className="definition-intro">
            Milk teeth play a crucial role in your child's development, helping them chew correctly, speak clearly, and guide permanent teeth into their proper positions. Protecting them is vital for long-term health.
          </p>
          <div className="extraction-types-flex">
            <div className="type-item" data-aos="fade-right" data-aos-delay="400">
              <div className="type-icon-wrapper"><FaBaby /></div>
              <h3>Infant Care</h3>
              <p>Establishing a "Dental Home" by the first birthday ensures early monitoring of jaw and tooth development.</p>
            </div>
            <div className="type-item" data-aos="fade-left" data-aos-delay="500">
              <div className="type-icon-wrapper"><FaChild /></div>
              <h3>Developing Smiles</h3>
              <p>As permanent teeth arrive, we protect them with advanced preventive shielding like sealants and fluoride.</p>
            </div>
          </div>
        </div>

        {/* KEY SERVICES SECTION */}
        <section className="crown-types-section">
          <div className="content" data-aos="fade-up">
            <SectionTitle title="Specialized Pediatric Services" />
            <p className="text_first">
              We offer a full spectrum of dental treatments designed specifically for the unique needs of children:
            </p>
          </div>

          <div className="crown-grid">
            {services.map((item, idx) => (
              <div className="crown-card" key={idx} data-aos="zoom-in" data-aos-delay={idx * 150}>
                <div className="icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* MIDDLE HIGHLIGHT SECTION */}
        <section className="why-choose-us">
          <div className="wrapper">
            <div className="image-side" data-aos="zoom-in" data-aos-delay="100">
              <img src={dentalImage} alt="Friendly Dental Care" />
            </div>

            <div className="text-side" data-aos="fade-up" data-aos-delay="300">
              <h2>A Gentle Touch for Little Smiles</h2>
              <p style={{ color: 'black' }}>
                At <strong>Kosmix Dental Clinic</strong>, HSR Layout, we understand that a child's first few dental visits can shape their lifelong perception of oral health. Our clinic is equipped with child-friendly tools and a staff trained to manage dental anxiety with patience and care.
              </p>
              <p style={{ color: 'black' }}>
                We use the latest diagnostic technology, including ultra-low dose digital imaging, to ensure your child's safety is never compromised. Our treatments are designed to be minimally invasive, prioritizing comfort and quick recovery.
              </p>
              <p style={{ color: 'black' }}>
                Beyond treatment, we empower parents with the knowledge they need to prevent cavities at home. From brushing techniques to nutritional advice, we are your partners in your child's dental wellness.
              </p>
              <p style={{ color: 'black' }}>
                <strong>Book a "Happy Visit" today</strong> and let us show your child that going to the dentist can be a fun and rewarding experience!
              </p>
            </div>
          </div>
        </section>

        {/* STEP-BY-STEP PROCESS */}
        <section className="timeline-container" style={{ overflowX: 'hidden', padding: '0 1rem' }}>
          <div className="content" data-aos="fade-up">
            <SectionTitle title="The Happy Child Dental Journey" />
            <p className="text_first">
              Our process is designed to build trust and ensure a stress-free visit for both child and parent:
            </p>
          </div>

          <div className="timeline-step" data-aos="fade-right">
            <div className="timeline-icon">🤝</div>
            <div className="timeline-content">
              <h3>The Warm Welcome</h3>
              <p>We introduce your child to the clinic environment using friendly terms, letting them explore the "magic chair" and tools.</p>
            </div>
            <div className="timeline-icon">1</div>
          </div>

          <div className="timeline-step" data-aos="fade-left">
            <div className="timeline-icon">🔍</div>
            <div className="timeline-content">
              <h3>Gentle Examination</h3>
              <p>A thorough check of teeth, gums, and jaw development using non-frightening techniques and light-hearted interaction.</p>
            </div>
            <div className="timeline-icon">2</div>
          </div>

          <div className="timeline-step" data-aos="fade-right">
            <div className="timeline-icon">🎨</div>
            <div className="timeline-content">
              <h3>Fun Education</h3>
              <p>We teach your child the "superhero" way to brush and floss using models and interactive demonstrations.</p>
            </div>
            <div className="timeline-icon">3</div>
          </div>

          <div className="timeline-step" data-aos="fade-left">
            <div className="timeline-icon">🛡️</div>
            <div className="timeline-content">
              <h3>Painless Treatment</h3>
              <p>If care is needed, we use specialized numbing gels and calming techniques to ensure the procedure is entirely pain-free.</p>
            </div>
            <div className="timeline-icon">4</div>
          </div>

          <div className="timeline-step" data-aos="fade-right">
            <div className="timeline-icon">🎁</div>
            <div className="timeline-content">
              <h3>The Bravo Reward</h3>
              <p>We celebrate every visit's success with stickers and oral health gifts, leaving your child excited for their next visit.</p>
            </div>
            <div className="timeline-icon">5</div>
          </div>
        </section>

        {/* BENEFITS SECTION */}
        <section className="advantages-section">
          <div className="content" data-aos="fade-up">
            <SectionTitle title="Why Parents Trust Kosmix for Their Kids" />
          </div>

          <div className="advantages-grid">
            {benefits.map((item, index) => (
              <div key={index} className="advantage-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <FaCheckCircle className="advantage-icon" />
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* WHY CHOOSE US HUB */}
        <section className="advantage-hub-section">
          <div className="container">
            <div className="hub-header" data-aos="fade-up">
              <span className="subtitle">Kid-Friendly Advantage</span>
              <SectionTitle title="A Specialized Approach to Pediatric Health" />
              <p className="description">
                We combine modern pediatric protocols with a compassionate environment to provide the best dental experience for your little ones in HSR Layout.
              </p>
            </div>

            <div className="advantage-grid">
              {reasons.map((reason, index) => (
                <motion.div
                  key={index}
                  className="advantage-item"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <div className="icon-sphere">
                    {reason.icon}
                    <div className="pulse"></div>
                  </div>
                  <div className="content-box">
                    <h3>{reason.title}</h3>
                    <p>{reason.description}</p>
                  </div>
                  <div className="line-connector"></div>
                </motion.div>
              ))}
            </div>

            <p className="importance-footer" data-aos="fade-up" data-aos-delay="100">
              Establishing good oral health habits in childhood is the best gift you can give your child. At Kosmix Dental Clinic, we make it our mission to ensure those habits start with a smile.
            </p>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="cta-section" data-aos='zoom-in'>
          <div className="cta-content">
            <h2>
              Ready for a Happy Visit? <span>Book Now!</span>
            </h2>
            <p>
              Schedule your child's appointment today at Kosmix Dental Clinic and let our pediatric experts protect their precious smile.
            </p>
            <div className="cta-buttons">
              <Link to="/#appointment" >
                <button className="btn-primary">Book Kids Visit</button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

const SectionTitle = ({ title }) => (
  <h2 className="section-title">{title}</h2>
);

export default ChildrenDentistry;
